import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetTitrationForProductQuery } from "src/api/services/titrationApi";
import { OneTimeProduct, ProductCategories } from "src/api/types";
import { theme } from "src/assets/theme";
import { useIsMobile } from "src/hooks/useIsMobile";
import WhiteCard from "src/shared/components/WhiteCard";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRecommendedDosage } from "src/context/RecommendedDosageProvider";
import { useLatestOrder } from "src/context/LatestOrder";
import { mapPhaseToMonths } from "src/pages/PatientDashboard/components/OneTimeProducts/components/ProductCard/utils/mapPhaseToMonths";
import { useLocation } from "react-router-dom";

interface CardProps {
  product: OneTimeProduct;
}

const mapProductToBgColor: Record<number, string> = {
  1: "#00B5B81A",
  2: "#00B5B833",
  3: "#00B5B84D",
  4: "#00B5B866",
  5: "#00B5B880",
  6: "#00B5B899",
};

const SubscriptionProductCard = ({ product }: CardProps) => {
  const isMobile = useIsMobile();
  const recommended = useRecommendedDosage();
  const latestOrder = useLatestOrder((o) => o);
  const { data: nextDose } = useGetTitrationForProductQuery(
    recommended != null &&
      latestOrder?.product_category.name === ProductCategories.WeightLoss
      ? latestOrder.product.slug
      : skipToken,
  );
  const { t } = useTranslation();
  const component = "oneTimeProducts.productCard";
  const { pathname } = useLocation();

  const recommendedDose = useMemo(() => {
    if (nextDose == null) return;

    if (nextDose.max_dose == null && nextDose.min_dose == null) return;

    const doseRange = `${nextDose.min_dose} - ${nextDose.max_dose}`;

    return (
      product.sort_order === nextDose.tier && product.dosage.includes(doseRange)
    );
  }, [nextDose, product.dosage, product.sort_order]);

  const monthText = useMemo(() => {
    if (product.price.number_of_months === 1) return "";

    const months =
      mapPhaseToMonths[`${product.product.slug}-${product.sort_order}`];

    return t(
      `${component}.${product.product.slug}.${product.sort_order}.months`,
      months,
    );
  }, [
    product.sort_order,
    product.price.number_of_months,
    product.product.slug,
    t,
  ]);

  if (!pathname.includes("care")) return null;

  return (
    <WhiteCard
      direction={isMobile ? "column" : "row"}
      justifyContent="space-between"
      sx={{
        backgroundColor: recommendedDose
          ? "#FFF20033"
          : (mapProductToBgColor[product.sort_order] ?? "#00B5B81A"),
      }}
    >
      <Stack gap={0.5}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction={"row"} spacing={0.5}>
            <Typography fontWeight={700}>
              {t(
                `${component}.${product.product.slug}.${product.dosage}.${product.sort_order}.label`,
                product.label,
              )}
            </Typography>
            <Typography>{monthText}</Typography>
          </Stack>
          {isMobile ? (
            <Typography fontWeight={600} color={theme.palette.secondary.main}>
              {t(`save {{savings}}`, { savings: product.savings })}
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="body2">
          {product.dosage} -{" "}
          <span style={{ fontStyle: "italic" }}>
            {t(
              `${component}.${product.product.slug}.${product.dosage}.${product.sort_order}.description`,
              product.description,
            )}
          </span>
        </Typography>
      </Stack>
    </WhiteCard>
  );
};

export default SubscriptionProductCard;
